import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core/common/event';
import type { AIDisclosureState, AIDisclosureResultState } from '../../types';
import { AIDisclosureParagraphType, AIDisclosureStep } from '../../enums';
import { AIDisclosureModel } from '../../models/disclosure-model';
import { AIDisclosureResultService } from '../disclosure-result-service';
import {
    GetTechMeansDTO,
    PutTechMeansDTO,
} from '@/domains/disclosure/repository/disclosure-repository';
import { TASK_TYPE } from '@/constants';
import { CreateTask } from '@/infrastructure/api-functions/core-drafting-api/create-task-api';
import { GetTaskDetail } from '@/infrastructure/api-functions/core-drafting-api/get-task-detail-api';
import { AIDisclosureService } from '../disclosure-service';
import type { GetTaskListRequest } from '@/domains/task/apis/task-api';
import _ from 'lodash';

export interface CreateDisclosureTaskContent {
    user_input: string
}


@injectable()
export class AIDisclosureServiceImpl implements AIDisclosureService {
    private _onStatesChangeEmitter = new Emitter<AIDisclosureState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    constructor(
        @inject(AIDisclosureModel)
        private _model: AIDisclosureModel,
        @inject(AIDisclosureResultService)
        private _disclosureResultService: AIDisclosureResultService,
        @inject(GetTechMeansDTO)
        private _getTechMeansDTO: GetTechMeansDTO,
        @inject(PutTechMeansDTO)
        private _putTechMeansDTO: PutTechMeansDTO,
        @inject(CreateTask)
        private _createTask: CreateTask<CreateDisclosureTaskContent>,
        @inject(GetTaskDetail)
        private _getTaskDetail: GetTaskDetail<AIDisclosureParagraphType>,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    private _setModelData(value: Partial<AIDisclosureState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public goResult() {
        this._setModelData({
            currentStep: AIDisclosureStep.DISCLOSURE_RESULT,
        });
        this._disclosureResultService.generateResult();
    }

    public async startGenerate(taskId: string): Promise<boolean> {
        const { input, currentStep } = this.getModelData();

        const goalSettings = await this._getTechMeansDTO.fetch({ taskId, currentStep, input });

        if (goalSettings.techMeans) {
            await this._disclosureResultService.updateSetting(goalSettings);
            return true;
        }
        return false;
    }

    public async putTechMeans(techMeans: string) {
        const { currentStep, input, taskId } = this.getModelData();

        await this.createTask(taskId, input);

        const goalSettings = await this._putTechMeansDTO.fetch({ taskId, currentStep, techMeans });

        if (goalSettings.techMeans) {
            await this._disclosureResultService.updateSetting(goalSettings);
        }
    }

    public updateInput(input: string) {
        this._setModelData({ input });
    }

    private _initDomainModel(modelData?: Partial<AIDisclosureState & AIDisclosureResultState>) {
        const { taskId = '', input = '', currentStep = AIDisclosureStep.DISCLOSURE_INPUT, stepDetail = {}, ...resultModelData } = modelData || {};

        this._setModelData({
            taskId,
            input,
            currentStep,
            stepDetail,
        });

        this._disclosureResultService.initModel(resultModelData);
    }

    public async createTask(taskId: string, input: string) {
        const params = {
            task_id: taskId,
            type: TASK_TYPE.DISCLOSURE as GetTaskListRequest['type'],
            content: {
                user_input: input,
            },
        };
        await this._createTask.createTask(params);
    }

    public async init(taskId: string) {
        this._getTaskDetail.getTaskDetail(taskId).then((res) => {
            const { current_step, task_id, step_detail, user_input } = res;
            if (!current_step) {
                this._initDomainModel({ taskId: task_id, input: user_input });
            } else {
                const hidden = _.map(step_detail[AIDisclosureStep.DISCLOSURE_RESULT], item => {
                    if (item.hide) {
                        return item.content_type;
                    }
                });
                this._initDomainModel({ taskId: task_id, input: user_input, currentStep: current_step as AIDisclosureStep, stepDetail: step_detail, hidden});
                current_step === AIDisclosureStep.DISCLOSURE_RESULT && this._disclosureResultService.generateResult();
            }
        });
    }
}
